import { useEffect, useState } from "react";
import axios from "axios";
import Button from "../Button";
import Input from "../Input/";
import RadioButton from "../RadioButton";

/** 'check' = initial lookup, 'found' = user exists (hide body), 'manual' = not found, show manual form */
const STATE_CHECK = "check";
const STATE_FOUND = "found";
const STATE_MANUAL = "manual";
const STATE_SELF_QUESTION = "self-question";

const MEMBERSHIP_CHECK_TIMEOUT_MS = 15_000;

export default function TicketBody({
  eventId,
  membershipStatusUrl,
  onMembershipFound,
  initialEmail = "",
  nonMemberPrice,
  askSelfTicketQuestion = false,
  onSelfTicketChoice,
  initialData,
}) {
  const isEditMode = !!initialData;
  const [state, setState] = useState(
    askSelfTicketQuestion && !isEditMode ? STATE_SELF_QUESTION : isEditMode ? STATE_MANUAL : STATE_CHECK
  );
  const [email, setEmail] = useState(initialData?.email ?? initialEmail);
  const [firstName, setFirstName] = useState(initialData?.first_name ?? "");
  const [lastName, setLastName] = useState(initialData?.last_name ?? "");
  const [company, setCompany] = useState(initialData?.company ?? "");
  const [title, setTitle] = useState(initialData?.title ?? "");
  const [selfChoice, setSelfChoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  useEffect(() => {
    if (state === STATE_SELF_QUESTION && !askSelfTicketQuestion) {
      setState(STATE_CHECK);
    }
  }, [askSelfTicketQuestion, state]);

  const handleSelfQuestionSubmit = (e) => {
    e.preventDefault();
    if (selfChoice == null) {
      setError("Please select yes or no.");
      return;
    }
    setError(null);
    if (selfChoice === true) {
      onSelfTicketChoice?.(true);
      return;
    }
    onSelfTicketChoice?.(false);
    setState(STATE_CHECK);
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();

    if (!email) {
      setError("Please enter an email address.");
      return;
    }
    if (!eventId || !membershipStatusUrl) {
      setError(
        "Membership lookup is unavailable. Please try refreshing the page.",
      );
      return;
    }

    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      MEMBERSHIP_CHECK_TIMEOUT_MS,
    );

    try {
      const headers = { "Content-Type": "application/json" };
      const nonce = window.wpApiSettings?.nonce;
      if (nonce) headers["X-WP-Nonce"] = nonce;

      const res = await axios.post(
        membershipStatusUrl,
        { email, event_id: eventId },
        { withCredentials: true, headers, signal: controller.signal },
      );

      const data = res.data;
      if (!data || typeof data !== "object") {
        throw new Error("Unexpected response from membership check.");
      }

      setCheckResult(data);

      if (data.user_id != null) {
        const applied = onMembershipFound?.(data);
        if (applied === false) {
          setError("This person is already registered.");
          return;
        }
        setState(STATE_FOUND);
      } else {
        setState(STATE_MANUAL);
      }
    } catch (err) {
      if (axios.isCancel?.(err) || err.name === "CanceledError") {
        setError("The request timed out. Please try again.");
      } else if (!navigator.onLine) {
        setError("You appear to be offline. Please check your connection.");
      } else {
        const status = err.response?.status;
        let msg;
        if (status === 422 || status === 400) {
          msg =
            err.response?.data?.message ||
            "Invalid email address. Please check and try again.";
        } else if (status >= 500) {
          msg = "Server error — please try again later.";
        } else {
          msg =
            err.response?.data?.message ||
            err.message ||
            "Failed to check membership status.";
        }
        setError(msg);
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!email || !firstName || !lastName || !company || !title) {
      setError("Please fill in all required fields.");
      return;
    }

    setError(null);
    const data = {
      email,
      first_name: firstName,
      last_name: lastName,
      company,
      title,
      user_id: null,
      membership_level: initialData?.member_type ?? "non-member",
      price: initialData?.price ?? checkResult?.price ?? "0",
    };
    const applied = onMembershipFound?.(data);
    if (applied === false) {
      setError("Entry already exists.");
    }
  };

  if (state === STATE_FOUND) {
    return null;
  }

  return (
    <div className="ticket__body">
      {state === STATE_SELF_QUESTION && (
        <form onSubmit={handleSelfQuestionSubmit}>
          {error && (
            <p className="ticket__body-error ticket__body-error--above" role="alert">
              {error}
            </p>
          )}
          <p className="ticket__text">Is this ticket for you?</p>
          <div className="ticket__radio">
            <RadioButton
              id="selfTicketYes"
              name="selfTicketChoice"
              checked={selfChoice === true}
              onChange={() => {
                setSelfChoice(true);
                if (error) setError(null);
              }}
              label="Yes"
            />
            <RadioButton
              id="selfTicketNo"
              name="selfTicketChoice"
              checked={selfChoice === false}
              onChange={() => {
                setSelfChoice(false);
                if (error) setError(null);
              }}
              label="No"
            />
          </div>
          <Button type="submit">Next</Button>
        </form>
      )}

      {state === STATE_CHECK && (
        <form onSubmit={handleCheckStatus}>
          {error && (
            <p className="ticket__body-error ticket__body-error--above" role="alert">
              {error}
            </p>
          )}
          <Input
            name="email"
            type="email"
            label="Email Address"
            placeholder="Email Address"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
          />
          <Button type="submit" loading={loading}>
            Check Member Status
          </Button>
          <p className="ticket__required">* Required</p>
        </form>
      )}

      {state === STATE_MANUAL && (
        <form onSubmit={handleManualSubmit}>
          <div className="" style={{ marginBottom: "0.75rem" }}>
            <p className="ticket__text">
              {isEditMode ? (
                "Edit attendee details below."
              ) : (
                <>
                  The email you've provided is not registered as a member in our
                  system. <br />
                  The pricing for a non-member ticket is{" "}
                  {"$" +
                    Number(
                      initialData?.price ??
                        nonMemberPrice ??
                        checkResult?.price ??
                        0,
                    )}
                  .
                </>
              )}
            </p>
          </div>
          {error && (
            <p className="ticket__body-error ticket__body-error--above" role="alert">
              {error}
            </p>
          )}
          <Input
            name="email"
            type="email"
            label="Email Address"
            placeholder="Email Address"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
          />
          <Input
            name="first_name"
            type="text"
            label="First Name"
            placeholder="First name"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            name="last_name"
            type="text"
            label="Last Name"
            placeholder="Last name"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          <Input
            name="company"
            type="text"
            label="Company"
            placeholder="Company"
            required
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <Input
            name="title"
            type="text"
            label="Title"
            placeholder="Job title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Button type="submit">Confirm</Button>
          <p className="ticket__required">* Required</p>
        </form>
      )}

    </div>
  );
}
